// PedagogicalQuality ("Pedagogical quality") — will this material teach well?
// Mounted in the right dock's focused-material view, fed by the pure adapter in
// `reviewQualityModel.ts`.
//
// Owner decisions 2026-10-01: the headline NAMES the issue ("The text is heavy
// to read"), never a count ("1 thing to improve"); a finding comes with what to
// do and where (the heaviest sections); raw numbers (LIX, word counts) sit
// behind a disclosure with a plain explanation. The concept-map and
// item-quality axes are not on the client wire yet, so they are not shown at
// all — no "coming soon" placeholders (a half-designed component is worse than
// none). They return as their own sections once their data is real.
//
// Pure: `pedagogyRead` folds the view-model into message descriptors; the
// component only localises and lays out. The `<details>` open state is the
// browser's own (view-ephemeral). Tokens only, no borders.

import { exhaustSwitchCase } from "@bilda/utils";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon, type IconName } from "../Icon/Icon.js";
import { Text } from "../Text/Text.js";
import "./PedagogicalQuality.css";

// === View-model ==============================================================

type LixBand = "very_easy" | "easy" | "medium" | "hard" | "very_hard";

// A section heavy enough to be worth simplifying.
export type HeavySection = {
    readonly id: string;
    readonly title: string;
};

type ReadabilityAxis =
    | { readonly type: "not_yet_checked" }
    | {
          readonly type: "checked";
          readonly lix: number;
          readonly band: LixBand;
          readonly words: number;
          readonly sentences: number;
          // Heaviest first; empty when no section stands out.
          readonly heaviestSections: readonly HeavySection[];
      };

export type PedagogicalQualityViewModel = {
    readonly readability: ReadabilityAxis;
};

export type PedagogicalQualityProps = {
    readonly model: PedagogicalQualityViewModel;
    // Opens the material at a heavy section (its node id). Absent ⇒ the
    // section titles read as plain text.
    readonly onOpenSection?: (id: string) => void;
    readonly className?: string;
};

// === Projection — view-model → what to say ==================================

export type PedagogyTone = "pending" | "clean" | "attention";

export type PedagogyRead = {
    readonly tone: PedagogyTone;
    readonly headline: MessageDescriptor;
    // The reading level in everyday terms ("Like factual non-fiction…").
    readonly level: MessageDescriptor | null;
    readonly suggestion: MessageDescriptor | null;
    readonly heaviestSections: readonly HeavySection[];
    // The raw measure, for the disclosure. Null before a check.
    readonly measure: MessageDescriptor | null;
};

const ATTENTION: ReadonlySet<LixBand> = new Set(["hard", "very_hard"]);

const headlineOf = (band: LixBand): MessageDescriptor => {
    switch (band) {
        case "very_easy":
            return msg`The text is very easy to read`;
        case "easy":
            return msg`The text is easy to read`;
        case "medium":
            return msg`The text is moderately easy to read`;
        case "hard":
            return msg`The text is heavy to read`;
        case "very_hard":
            return msg`The text is very heavy to read`;
        default:
            return exhaustSwitchCase(band);
    }
};

// Björnsson's conventional LIX scale, in the reader's terms.
const levelOf = (band: LixBand): MessageDescriptor => {
    switch (band) {
        case "very_easy":
            return msg`About the level of a children's book.`;
        case "easy":
            return msg`About the level of simple everyday text.`;
        case "medium":
            return msg`About the level of ordinary news text.`;
        case "hard":
            return msg`About the level of factual non-fiction – heavy for many readers.`;
        case "very_hard":
            return msg`About the level of academic or official prose – heavy for most readers.`;
        default:
            return exhaustSwitchCase(band);
    }
};

export const pedagogyRead = (m: PedagogicalQualityViewModel): PedagogyRead => {
    const r = m.readability;
    if (r.type === "not_yet_checked") {
        return {
            tone: "pending",
            headline: msg`Not checked yet`,
            level: null,
            suggestion: null,
            heaviestSections: [],
            measure: null,
        };
    }
    const attention = ATTENTION.has(r.band);
    const lix = Math.round(r.lix);
    return {
        tone: attention ? "attention" : "clean",
        headline: headlineOf(r.band),
        level: levelOf(r.band),
        suggestion: attention
            ? r.heaviestSections.length > 0
                ? msg`Shorten the longest sentences and swap the hardest words for simpler ones. Start with these sections:`
                : msg`Shorten the longest sentences and swap the hardest words for simpler ones.`
            : null,
        heaviestSections: attention ? r.heaviestSections : [],
        measure: msg`LIX ${lix}, from ${r.words} words in ${r.sentences} sentences. LIX (readability index) adds the average sentence length to the share of long words: below 30 reads easily, above 50 is heavy.`,
    };
};

// === Component ===============================================================

const ICON: Record<PedagogyTone, IconName> = {
    pending: "clock",
    clean: "check",
    attention: "paragraph",
};

export const PedagogicalQuality = ({
    model,
    onOpenSection,
    className,
}: PedagogicalQualityProps): ReactNode => {
    const { i18n, t } = useLingui();
    const read = pedagogyRead(model);
    return (
        <section
            className={`pq-panel${className ? ` ${className}` : ""}`}
            aria-label={t`Pedagogical quality`}
        >
            <header className="pq-header">
                <div className="pq-headline">
                    <Icon
                        name={ICON[read.tone]}
                        size="md"
                        className={`pq-headlineIcon--${read.tone}`}
                    />
                    <Text as="p" size="lg" weight="semibold">
                        {i18n._(read.headline)}
                    </Text>
                </div>
                {read.level !== null ? (
                    <Text as="p" size="sm" tone="dim">
                        {i18n._(read.level)}
                    </Text>
                ) : null}
            </header>
            {read.suggestion !== null ? (
                <div className="pq-card">
                    <Text as="p" size="sm">
                        {i18n._(read.suggestion)}
                    </Text>
                    {read.heaviestSections.length > 0 ? (
                        <ul className="pq-sectionList">
                            {read.heaviestSections.map((s) => (
                                <li key={s.id}>
                                    {onOpenSection !== undefined ? (
                                        <button
                                            type="button"
                                            className="pq-sectionLink"
                                            onClick={() => onOpenSection(s.id)}
                                        >
                                            {s.title}
                                        </button>
                                    ) : (
                                        <Text
                                            as="span"
                                            size="sm"
                                            weight="medium"
                                        >
                                            {s.title}
                                        </Text>
                                    )}
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </div>
            ) : null}
            {read.measure !== null ? (
                <details className="pq-measure">
                    <summary>
                        <Text as="span" size="xs" tone="dim">
                            {t`How this is measured`}
                        </Text>
                    </summary>
                    <Text as="p" size="xs" tone="dim">
                        {i18n._(read.measure)}
                    </Text>
                </details>
            ) : null}
        </section>
    );
};
